import React, { useState, useEffect, useRef, useCallback } from 'react';
import MDEditor from '@uiw/react-md-editor';
import {
  Sparkles, Upload, Loader2, File, Trash2, Check,
  AlertTriangle, FileText, Search, ChevronsUpDown,
  Mail, Send, SkipForward
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { expedientesAPI, adjuntosAPI } from '../api';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Confidence badge colors
const CONFIANZA_COLORS = {
  alta: 'bg-green-100 text-green-800 border-green-300',
  media: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  baja: 'bg-red-100 text-red-800 border-red-300',
};

const NuevaActuacionIA = ({ expedienteId, onSuccess, open, onOpenChange }) => {
  const [step, setStep] = useState('upload'); // upload | analyzing | review | email_sentencia | datos_faltantes
  const [pdfFile, setPdfFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // AI suggestion fields
  const [aiResult, setAiResult] = useState(null);
  const [titulo, setTitulo] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [selectedExpedienteId, setSelectedExpedienteId] = useState(expedienteId || null);
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);

  // Datos faltantes del expediente
  const [datosFaltantes, setDatosFaltantes] = useState([]);
  const [datosSeleccionados, setDatosSeleccionados] = useState({});
  const [actualizandoDatos, setActualizandoDatos] = useState(false);

  // Email sentencia state
  const [emailData, setEmailData] = useState(null);
  const [emailAsunto, setEmailAsunto] = useState('');
  const [emailCuerpo, setEmailCuerpo] = useState('');
  const [emailDestinatario, setEmailDestinatario] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const pendingDatosFaltantesRef = useRef([]);

  // Expediente combobox con búsqueda en servidor
  const [expedientes, setExpedientes] = useState([]);
  const [expedienteComboOpen, setExpedienteComboOpen] = useState(false);
  const [expedienteSearchTerm, setExpedienteSearchTerm] = useState('');
  const [selectedExpedienteLabel, setSelectedExpedienteLabel] = useState('');
  const [searchingExp, setSearchingExp] = useState(false);
  const searchTimerRef = useRef(null);

  const searchExpedientesAPI = useCallback((term) => {
    if (!term || term.length < 2) {
      setExpedientes([]);
      return;
    }
    setSearchingExp(true);
    expedientesAPI.getAll({ search: term, limit: 30 }).then(res => {
      let data;
      if (res.data && res.data.data) data = res.data.data;
      else if (Array.isArray(res.data)) data = res.data;
      else data = [];
      setExpedientes(data);
    }).catch(() => setExpedientes([])).finally(() => setSearchingExp(false));
  }, []);

  const handleExpSearchChange = (value) => {
    setExpedienteSearchTerm(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchExpedientesAPI(value), 300);
  };

  const resetState = useCallback(() => {
    setStep('upload');
    setPdfFile(null);
    setAnalyzing(false);
    setSaving(false);
    setAiResult(null);
    setTitulo('');
    setObservaciones('');
    setSelectedExpedienteId(expedienteId || null);
    setSelectedExpedienteLabel('');
    setFecha(new Date().toISOString().split('T')[0]);
    setExpedienteSearchTerm('');
    setExpedienteComboOpen(false);
    setExpedientes([]);
    setDatosFaltantes([]);
    setDatosSeleccionados({});
    setActualizandoDatos(false);
    setEmailData(null);
    setEmailAsunto('');
    setEmailCuerpo('');
    setEmailDestinatario('');
    setSendingEmail(false);
    setGeneratingEmail(false);
    pendingDatosFaltantesRef.current = [];
  }, [expedienteId]);

  const handleFileSelect = (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast({ title: "Error", description: "Solo se aceptan archivos PDF", variant: "destructive" });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast({ title: "Error", description: "El archivo no puede superar 20 MB", variant: "destructive" });
      return;
    }
    setPdfFile(file);
  };

  const handleAnalyze = async () => {
    if (!pdfFile) return;
    setAnalyzing(true);
    setStep('analyzing');

    const formData = new FormData();
    formData.append('file', pdfFile);
    if (expedienteId) {
      formData.append('expediente_id', expedienteId);
    }

    try {
      const token = localStorage.getItem('lexduo_token');
      const response = await fetch(`${API}/actuaciones-ia/analizar-pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Error analizando el PDF');
      }

      const data = await response.json();
      setAiResult(data);
      setTitulo(data.titulo || '');
      setObservaciones(data.observaciones || '');
      setFecha(new Date().toISOString().split('T')[0]);

      if (expedienteId) {
        // Si venimos de un expediente concreto, asociar directamente
        setSelectedExpedienteId(expedienteId);
        setSelectedExpedienteLabel(`Expediente actual`);
      } else if (data.expediente_id) {
        setSelectedExpedienteId(data.expediente_id);
        setSelectedExpedienteLabel(
          data.expediente_numero
            ? `${data.expediente_numero}`
            : `Expediente #${data.expediente_id}`
        );
      }

      setStep('review');
    } catch (error) {
      toast({
        title: "Error en análisis IA",
        description: error.message,
        variant: "destructive"
      });
      setStep('upload');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSelectExpediente = (expId) => {
    const exp = expedientes.find(e => String(e.id) === expId);
    if (exp) {
      setSelectedExpedienteId(exp.id);
      setSelectedExpedienteLabel(`${exp.numero_expediente} - ${exp.titulo}`);
    }
    setExpedienteComboOpen(false);
    setExpedienteSearchTerm('');
  };

  const handleSave = async () => {
    if (!selectedExpedienteId) {
      toast({ title: "Error", description: "Selecciona un expediente", variant: "destructive" });
      return;
    }
    if (!titulo.trim()) {
      toast({ title: "Error", description: "El título es obligatorio", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      // 1. Create actuación
      const actuacionData = {
        titulo: titulo.trim().toUpperCase(),
        fecha: new Date(fecha).toISOString(),
        tipo: aiResult?.tipo_documento || 'general',
        descripcion: observaciones || 'Sin descripción',
        observaciones: observaciones,
      };

      const res = await expedientesAPI.createActuacion(selectedExpedienteId, actuacionData);
      const newActuacion = res.data;

      // 2. Upload PDF as attachment
      if (pdfFile && newActuacion?.id) {
        const uploadData = new FormData();
        uploadData.append('file', pdfFile);
        uploadData.append('tipo_adjunto', 'documento');
        try {
          await adjuntosAPI.uploadActuacion(newActuacion.id, uploadData);
        } catch (err) {
          console.error('Error subiendo PDF:', err);
          toast({ title: "Aviso", description: "Actuación creada pero hubo un error al adjuntar el PDF", variant: "default" });
        }
      }

      toast({ title: "Actuación creada con IA", description: `"${titulo}" creada correctamente` });

      // 3. Detectar datos faltantes del expediente (almacenar para después del email)
      let detectedDatosFaltantes = [];
      if (aiResult?.datos_expediente && selectedExpedienteId) {
        try {
          const token = localStorage.getItem('lexduo_token');
          const detectForm = new FormData();
          detectForm.append('expediente_id', selectedExpedienteId);
          detectForm.append('datos_expediente', JSON.stringify(aiResult.datos_expediente));

          const detectRes = await fetch(`${API}/actuaciones-ia/detectar-datos-faltantes`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: detectForm,
          });

          if (detectRes.ok) {
            const detectData = await detectRes.json();
            detectedDatosFaltantes = detectData.datos_faltantes || [];
          }
        } catch (err) {
          console.error('Error detectando datos faltantes:', err);
        }
      }

      // 4. Si es SENTENCIA → generar borrador de email al cliente
      if (aiResult?.tipo_documento === 'sentencia' && selectedExpedienteId) {
        pendingDatosFaltantesRef.current = detectedDatosFaltantes;
        setGeneratingEmail(true);
        setStep('email_sentencia');
        setSaving(false);

        try {
          const token = localStorage.getItem('lexduo_token');
          const emailRes = await fetch(`${API}/actuaciones-ia/generar-email-sentencia`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              expediente_id: selectedExpedienteId,
              observaciones: observaciones,
              titulo: titulo,
            }),
          });

          const data = await emailRes.json();
          if (data.success) {
            setEmailData(data);
            setEmailAsunto(data.asunto);
            setEmailCuerpo(data.cuerpo);
            setEmailDestinatario(data.destinatario_email);
          } else {
            toast({
              title: "Aviso",
              description: data.message || "No s'ha pogut generar l'email. Podeu ometre aquest pas.",
              variant: "default",
            });
          }
        } catch (err) {
          console.error('Error generando email sentencia:', err);
          toast({ title: "Aviso", description: "Error generant l'email de sentència", variant: "default" });
        } finally {
          setGeneratingEmail(false);
        }
        return;
      }

      // 5. Si NO es sentencia → flujo normal de datos faltantes
      if (detectedDatosFaltantes.length > 0) {
        setDatosFaltantes(detectedDatosFaltantes);
        const sel = {};
        detectedDatosFaltantes.forEach((d, i) => { sel[i] = true; });
        setDatosSeleccionados(sel);
        setStep('datos_faltantes');
        setSaving(false);
        return;
      }

      // Si no hay nada más, cerrar
      resetState();
      onOpenChange(false);
      if (onSuccess) onSuccess(selectedExpedienteId);
    } catch (error) {
      console.error('Error guardando actuación:', error);
      toast({
        title: "Error",
        description: "No se pudo crear la actuación",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // ===== Funciones de email sentencia =====
  const proceedAfterEmail = () => {
    const pending = pendingDatosFaltantesRef.current;
    if (pending && pending.length > 0) {
      setDatosFaltantes(pending);
      const sel = {};
      pending.forEach((d, i) => { sel[i] = true; });
      setDatosSeleccionados(sel);
      setStep('datos_faltantes');
    } else {
      resetState();
      onOpenChange(false);
      if (onSuccess) onSuccess(selectedExpedienteId);
    }
  };

  const handleSendEmail = async () => {
    if (!emailDestinatario || !emailCuerpo.trim()) {
      toast({ title: "Error", description: "Falten dades per enviar l'email", variant: "destructive" });
      return;
    }

    setSendingEmail(true);
    try {
      const token = localStorage.getItem('lexduo_token');
      const formData = new FormData();
      formData.append('expediente_id', selectedExpedienteId);
      formData.append('destinatario_email', emailDestinatario);
      formData.append('asunto', emailAsunto);
      formData.append('cuerpo', emailCuerpo);
      if (pdfFile) {
        formData.append('file', pdfFile);
      }

      const res = await fetch(`${API}/actuaciones-ia/enviar-email-sentencia`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (res.ok) {
        const result = await res.json();
        toast({
          title: "Email enviat",
          description: result.message || `Email enviat correctament a ${emailDestinatario}`,
        });
      } else {
        const err = await res.json().catch(() => ({}));
        toast({
          title: "Error enviant email",
          description: err.detail || "No s'ha pogut enviar l'email",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error('Error enviando email sentencia:', err);
      toast({ title: "Error", description: "Error de connexió enviant l'email", variant: "destructive" });
    } finally {
      setSendingEmail(false);
      proceedAfterEmail();
    }
  };

  const handleSkipEmail = () => {
    proceedAfterEmail();
  };

  const handleIncorporarDatos = async () => {
    const datosAIncorporar = datosFaltantes
      .filter((_, i) => datosSeleccionados[i])
      .map(d => ({
        campo: d.campo,
        valor_detectado: d.valor_detectado,
        tipo: d.tipo,
        rol_participante: d.rol_participante,
        contacto_id_existente: d.contacto_id_existente || null,
      }));

    if (datosAIncorporar.length === 0) {
      handleSkipDatos();
      return;
    }

    setActualizandoDatos(true);
    try {
      const token = localStorage.getItem('lexduo_token');
      const res = await fetch(`${API}/actuaciones-ia/actualizar-expediente-datos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          expediente_id: selectedExpedienteId,
          datos: datosAIncorporar,
        }),
      });

      if (res.ok) {
        const result = await res.json();
        toast({
          title: "Expediente actualizado",
          description: `Se incorporaron ${result.actualizados.length} dato(s) al expediente`,
        });
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: "Error", description: err.detail || "No se pudieron incorporar los datos", variant: "destructive" });
      }
    } catch (err) {
      console.error('Error actualizando expediente:', err);
      toast({ title: "Error", description: "Error al incorporar datos", variant: "destructive" });
    } finally {
      setActualizandoDatos(false);
      resetState();
      onOpenChange(false);
      if (onSuccess) onSuccess(selectedExpedienteId);
    }
  };

  const handleSkipDatos = () => {
    resetState();
    onOpenChange(false);
    if (onSuccess) onSuccess(selectedExpedienteId);
  };

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Nueva Actuación con IA
          </DialogTitle>
        </DialogHeader>

        {/* STEP 1: Upload PDF */}
        {step === 'upload' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Sube un documento PDF judicial y la IA analizará su contenido para crear automáticamente una actuación con título, observaciones y asociación al expediente correcto.
            </p>

            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".pdf"
              onChange={(e) => handleFileSelect(Array.from(e.target.files || []))}
            />
            <div
              data-testid="ia-pdf-dropzone"
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.add('border-amber-500', 'bg-amber-50');
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('border-amber-500', 'bg-amber-50');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('border-amber-500', 'bg-amber-50');
                handleFileSelect(Array.from(e.dataTransfer.files));
              }}
            >
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700">Arrastra un archivo PDF aquí o haz clic para seleccionar</p>
              <p className="text-xs text-gray-400 mt-1">Máximo 20 MB - Solo archivos PDF con texto extraíble</p>
            </div>

            {pdfFile && (
              <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{pdfFile.name}</p>
                    <p className="text-xs text-gray-500">{(pdfFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); setPdfFile(null); }}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancelar</Button>
              <Button
                data-testid="ia-analyze-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                disabled={!pdfFile}
                onClick={handleAnalyze}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Analizar con IA
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* STEP 2: Analyzing */}
        {step === 'analyzing' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="relative">
              <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
              <Sparkles className="w-5 h-5 text-amber-400 absolute -top-1 -right-1 animate-pulse" />
            </div>
            <div className="text-center">
              <p className="font-medium text-gray-800">Analizando documento...</p>
              <p className="text-sm text-gray-500 mt-1">Extrayendo texto e identificando expediente con GPT-4o</p>
            </div>
          </div>
        )}

        {/* STEP 3: Review */}
        {step === 'review' && aiResult && (
          <div className="space-y-4">
            {/* AI confidence indicator */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
              <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <span className="text-sm text-gray-600">Análisis completado</span>
              <Badge className={`text-xs border ${CONFIANZA_COLORS[aiResult.confianza] || CONFIANZA_COLORS.baja}`}>
                Confianza: {aiResult.confianza}
              </Badge>
              {aiResult.tipo_documento && (
                <Badge variant="secondary" className="text-xs">
                  {aiResult.tipo_documento.replace(/_/g, ' ')}
                </Badge>
              )}
              {aiResult.referencia_encontrada && (
                <span className="text-xs text-gray-500 ml-auto">
                  Ref: {aiResult.referencia_encontrada}
                </span>
              )}
            </div>

            {/* Expediente selector */}
            <div>
              <Label className="flex items-center gap-1 mb-1">
                Expediente asociado *
                {!selectedExpedienteId && (
                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                )}
              </Label>
              {expedienteId ? (
                /* Si venimos de un expediente concreto, mostrar fijo sin opción de cambiar */
                <div className="p-2 bg-green-50 border border-green-200 rounded text-sm">
                  <Check className="w-4 h-4 text-green-600 inline mr-1" />
                  Expediente actual (asociado automáticamente)
                </div>
              ) : selectedExpedienteId && selectedExpedienteLabel ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 p-2 bg-green-50 border border-green-200 rounded text-sm">
                    <Check className="w-4 h-4 text-green-600 inline mr-1" />
                    {selectedExpedienteLabel}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedExpedienteId(null);
                      setSelectedExpedienteLabel('');
                    }}
                  >
                    Cambiar
                  </Button>
                </div>
              ) : (
                <Popover open={expedienteComboOpen} onOpenChange={setExpedienteComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={expedienteComboOpen}
                      className="w-full justify-between font-normal"
                      data-testid="ia-expediente-search"
                    >
                      Buscar expediente...
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[500px] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Escribe número o título del expediente..."
                        value={expedienteSearchTerm}
                        onValueChange={handleExpSearchChange}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {searchingExp
                            ? "Buscando..."
                            : expedienteSearchTerm.length < 2
                              ? "Escribe al menos 2 caracteres para buscar"
                              : "No se encontraron expedientes"}
                        </CommandEmpty>
                        <CommandGroup>
                          {expedientes.map(exp => (
                            <CommandItem
                              key={exp.id}
                              value={String(exp.id)}
                              onSelect={(val) => handleSelectExpediente(val)}
                              className="cursor-pointer"
                            >
                              <Check className={`mr-2 h-4 w-4 ${selectedExpedienteId === exp.id ? "opacity-100" : "opacity-0"}`} />
                              <span className="font-medium text-blue-600 mr-2">{exp.numero_expediente}</span>
                              <span className="truncate text-gray-600">{exp.titulo}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
              {aiResult.confianza === 'baja' && !selectedExpedienteId && (
                <p className="text-xs text-amber-600 mt-1">
                  La IA no pudo identificar el expediente con certeza. Selecciona uno manualmente.
                </p>
              )}
            </div>

            {/* Title */}
            <div>
              <Label>Título * (siempre en MAYÚSCULAS)</Label>
              <Input
                data-testid="ia-titulo-input"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value.toUpperCase())}
                placeholder="Título de la actuación"
                className="uppercase"
              />
            </div>

            {/* Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha</Label>
                <Input
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                />
              </div>
              <div>
                <Label>Archivo adjunto</Label>
                <div className="flex items-center gap-2 p-2 bg-gray-50 border rounded text-sm mt-1">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="truncate">{pdfFile?.name}</span>
                </div>
              </div>
            </div>

            {/* Observaciones */}
            <div data-color-mode="light">
              <Label>Observaciones (resumen IA)</Label>
              <div className="mt-1">
                <MDEditor
                  value={observaciones}
                  onChange={setObservaciones}
                  preview="edit"
                  height={250}
                  textareaProps={{
                    placeholder: 'Resumen generado por IA...'
                  }}
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleClose}>Cancelar</Button>
              <Button
                variant="outline"
                onClick={() => { setStep('upload'); setPdfFile(null); setAiResult(null); }}
              >
                Nuevo análisis
              </Button>
              <Button
                data-testid="ia-save-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={saving || !selectedExpedienteId || !titulo.trim()}
                onClick={handleSave}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Guardar actuación
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* STEP 4: Email comunicació de sentència */}
        {step === 'email_sentencia' && (
          <div className="space-y-4">
            {generatingEmail ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <div className="relative">
                  <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
                  <Mail className="w-5 h-5 text-blue-400 absolute -top-1 -right-1 animate-pulse" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-gray-800">Generant borrador d'email...</p>
                  <p className="text-sm text-gray-500 mt-1">La IA prepara la comunicació de sentència en català</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Mail className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-800">Comunicació de sentència al client</p>
                    <p className="text-sm text-blue-600">La IA ha preparat un borrador d'email. Reviseu-lo i editeu-lo abans d'enviar.</p>
                  </div>
                </div>

                {(!emailData || !emailData.success) && !emailDestinatario && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      {emailData?.message || "No s'ha pogut generar l'email automàticament. Podeu ometre aquest pas."}
                    </p>
                  </div>
                )}

                {(emailDestinatario || emailData?.success) && (
                  <>
                    <div>
                      <Label>Destinatari</Label>
                      <Input
                        data-testid="email-sentencia-destinatario"
                        value={emailDestinatario}
                        onChange={(e) => setEmailDestinatario(e.target.value)}
                        placeholder="email@client.com"
                        type="email"
                      />
                      {emailData?.nombre_cliente && (
                        <p className="text-xs text-gray-500 mt-1">Client: {emailData.nombre_cliente}</p>
                      )}
                    </div>

                    <div>
                      <Label>Assumpte</Label>
                      <Input
                        data-testid="email-sentencia-asunto"
                        value={emailAsunto}
                        onChange={(e) => setEmailAsunto(e.target.value)}
                        placeholder="Assumpte de l'email"
                      />
                    </div>

                    <div>
                      <Label>Cos de l'email</Label>
                      <Textarea
                        data-testid="email-sentencia-cuerpo"
                        value={emailCuerpo}
                        onChange={(e) => setEmailCuerpo(e.target.value)}
                        rows={18}
                        className="font-mono text-sm leading-relaxed"
                        placeholder="Contingut de l'email..."
                      />
                    </div>

                    {pdfFile && (
                      <div className="flex items-center gap-2 p-2 bg-gray-50 border rounded text-sm">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-600">Arxiu adjunt:</span>
                        <span className="font-medium">{pdfFile.name}</span>
                      </div>
                    )}
                  </>
                )}

                <DialogFooter className="gap-2">
                  <Button
                    variant="outline"
                    onClick={handleSkipEmail}
                    data-testid="email-sentencia-skip-btn"
                  >
                    <SkipForward className="w-4 h-4 mr-2" />
                    Ometre
                  </Button>
                  {(emailDestinatario || emailData?.success) && (
                    <Button
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={sendingEmail || !emailDestinatario || !emailCuerpo.trim()}
                      onClick={handleSendEmail}
                      data-testid="email-sentencia-send-btn"
                    >
                      {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      Enviar al client
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </div>
        )}

        {/* STEP 5: Datos faltantes del expediente */}
        {step === 'datos_faltantes' && datosFaltantes.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">Datos detectados en el documento</p>
                <p className="text-sm text-amber-600">La IA ha detectado datos que no constan en el expediente. Selecciona los que quieras incorporar.</p>
              </div>
            </div>

            <div className="space-y-2">
              {datosFaltantes.map((dato, idx) => (
                <label
                  key={idx}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    datosSeleccionados[idx]
                      ? 'bg-blue-50 border-blue-300'
                      : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                  data-testid={`dato-faltante-${dato.campo}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 w-4 h-4 rounded"
                    checked={!!datosSeleccionados[idx]}
                    onChange={(e) => setDatosSeleccionados(prev => ({ ...prev, [idx]: e.target.checked }))}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {dato.tipo === 'participante' ? 'Participante' : 'Expediente'}
                      </Badge>
                      <span className="font-medium text-sm">{dato.etiqueta}</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1 break-words">{dato.valor_detectado}</p>
                    {dato.contacto_id_existente ? (
                      <p className="text-xs text-green-700 mt-0.5 font-medium">
                        Coincide con registro existente: "{dato.nombre_existente}" — se vinculará al expediente
                      </p>
                    ) : dato.tipo === 'participante' ? (
                      <p className="text-xs text-gray-500 mt-0.5">
                        No se encontró en contactos existentes. Se creará automáticamente.
                      </p>
                    ) : dato.campo === 'juzgado' ? (
                      <p className="text-xs text-gray-500 mt-0.5">
                        No se encontró en organismos existentes. Se creará automáticamente.
                      </p>
                    ) : null}
                  </div>
                </label>
              ))}
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={handleSkipDatos}
                data-testid="ia-skip-datos-btn"
              >
                Omitir
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={actualizandoDatos || !Object.values(datosSeleccionados).some(v => v)}
                onClick={handleIncorporarDatos}
                data-testid="ia-incorporar-datos-btn"
              >
                {actualizandoDatos ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Incorporar datos al expediente
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NuevaActuacionIA;
